// The website header settings (header_configs.siteHeader, version 2) as the dashboard holds them.
//
// normalizeSiteHeader is a line-for-line port of backend/controllers/layout/siteHeader.js, so the
// screen works before the first load lands and against a backend that still serves version 1
// (flat popularDestinations, holidayTypes.mode, string labels): both read as the same complete
// version 2 object the server would build. Keep the two in step; a key missing here is a
// setting the screen cannot show.
//
// The server-computed parts of a GET (menus.holidayTypes.resolved, announcement.live) are not
// settings, so they never enter the editor's state and never go back in a save.

export const SITE_HEADER_VERSION = 2;

export const MAX_MENU_ITEMS = 12;
export const MAX_CUSTOM_MENUS = 3;
export const MAX_MOBILE_ITEMS = 12;
export const MAX_LABEL = 60;
export const MAX_SUB = 80;
export const MAX_TEXT = 200;
export const MAX_URL = 500;

export const SHOW_ON = ['all', 'desktop', 'mobile'];
export const ANNOUNCEMENT_TONES = ['orange', 'blue', 'dark', 'green'];
export const SUPPORT_ICONS = ['phone', 'help', 'chat', 'mail'];
export const DESTINATION_SOURCES = ['homepage', 'manual'];
export const HOLIDAY_TYPE_SOURCES = ['auto', 'manual'];
export const ACCOUNT_MENU_KEYS = ['bookings', 'profile', 'favourites'];

// The phone menu's built-in rows. `url` is the default; the dashboard may override it.
export const SERVICE_ITEMS = [
  { key: 'holidays', url: '/' },
  { key: 'flights', url: '/flights' },
  { key: 'hotels', url: '/hotels' },
  { key: 'transfers', url: '/transfers' },
];
const SERVICE_URL = new Map(SERVICE_ITEMS.map((s) => [s.key, s.url]));

// ─── primitives ─────────────────────────────────────────────────────────────

const bool = (v, fallback) => (typeof v === 'boolean' ? v : fallback);
const str = (v, max) => String(v ?? '').trim().slice(0, max);
const oneOf = (v, list, fallback) => (list.includes(v) ? v : fallback);
const clampInt = (v, min, max, fallback) => {
  const n = Number(v);
  if (!Number.isInteger(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};
const iconName = (v) => {
  const s = str(v, 64).toLowerCase();
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(s) ? s : '';
};
const isoDate = (v) => {
  const s = str(v, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

/** The server's link rule: paths, anchors, http(s), mailto and tel. Anything else is ''. */
export const safeUrl = (v) => {
  const s = str(v, MAX_URL);
  if (!s) return '';
  if (s.startsWith('//')) return `https:${s}`;
  if (s.startsWith('/') || s.startsWith('#')) return s;
  if (/^(https?:|mailto:|tel:)/i.test(s)) return s;
  return '';
};

/** True when a typed link would survive the save: empty, or one the server accepts. */
export const isValidLink = (v) => !String(v ?? '').trim() || safeUrl(v) !== '';

/** Bilingual text. A string is the same text in both languages. */
export const toText = (v, max = MAX_LABEL) => {
  if (typeof v === 'string') {
    const s = str(v, max);
    return { nl: s, en: s };
  }
  const o = v && typeof v === 'object' ? v : {};
  return { nl: str(o.nl, max), en: str(o.en, max) };
};

export const emptyText = () => ({ nl: '', en: '' });
export const hasText = (t) => Boolean(String(t?.nl ?? '').trim() || String(t?.en ?? '').trim());

const itemId = (v, fallback) => {
  const s = str(v, 40);
  return /^[A-Za-z0-9_-]+$/.test(s) ? s : fallback;
};

const uniqueIds = (items) => {
  const seen = new Set();
  return items.map((it, i) => {
    let id = it.id;
    while (seen.has(id)) id = `${it.id}-${i}`;
    seen.add(id);
    return id === it.id ? it : { ...it, id };
  });
};

const footerLink = (v) => {
  const o = v && typeof v === 'object' ? v : {};
  return { enabled: bool(o.enabled, false), label: toText(o.label), url: safeUrl(o.url) };
};

const dest = (d) => {
  if (!d || typeof d !== 'object') return null;
  if (d.type !== 'country' && d.type !== 'city') return null;
  const code = str(d.code, 20).toUpperCase();
  if (!code) return null;
  return {
    type: d.type,
    code,
    name: str(d.name, 120),
    countryName: str(d.countryName, 120),
  };
};

const positiveId = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// ─── blocks ─────────────────────────────────────────────────────────────────

function normalizeAnnouncement(v) {
  const o = v && typeof v === 'object' ? v : {};
  return {
    enabled: bool(o.enabled, false),
    text: toText(o.text, MAX_TEXT),
    linkLabel: toText(o.linkLabel),
    link: safeUrl(o.link),
    newTab: bool(o.newTab, false),
    tone: oneOf(o.tone, ANNOUNCEMENT_TONES, 'orange'),
    dismissible: bool(o.dismissible, true),
    startDate: isoDate(o.startDate),
    endDate: isoDate(o.endDate),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeDestinationsMenu(v) {
  const o = v && typeof v === 'object' ? v : {};
  const items = (Array.isArray(o.items) ? o.items : [])
    .map((it, i) => ({
      id: itemId(it?.id, `d${i + 1}`),
      active: bool(it?.active, true),
      label: toText(it?.label),
      sub: toText(it?.sub, MAX_SUB),
      icon: iconName(it?.icon),
      dest: dest(it?.dest),
      boardCode: str(it?.boardCode, 10).toUpperCase(),
      holidayTypeId: positiveId(it?.holidayTypeId),
      url: safeUrl(it?.url),
    }))
    .filter((it) => it.dest || it.url || it.holidayTypeId || it.boardCode)
    .slice(0, MAX_MENU_ITEMS);
  return {
    enabled: bool(o.enabled, true),
    label: toText(o.label),
    source: oneOf(o.source, DESTINATION_SOURCES, 'homepage'),
    maxItems: clampInt(o.maxItems, 1, MAX_MENU_ITEMS, 7),
    showGroupAsSub: bool(o.showGroupAsSub, true),
    items: uniqueIds(items),
    footerLink: footerLink(o.footerLink),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeHolidayTypesMenu(v) {
  const o = v && typeof v === 'object' ? v : {};
  const seen = new Set();
  const items = (Array.isArray(o.items) ? o.items : [])
    .map((it) => ({
      holidayTypeId: positiveId(it?.holidayTypeId),
      label: toText(it?.label),
      sub: toText(it?.sub, MAX_SUB),
      icon: iconName(it?.icon),
    }))
    .filter((it) => {
      if (!it.holidayTypeId || seen.has(it.holidayTypeId)) return false;
      seen.add(it.holidayTypeId);
      return true;
    })
    .slice(0, MAX_MENU_ITEMS);
  return {
    enabled: bool(o.enabled, true),
    label: toText(o.label),
    // `mode` is the version 1 name of `source`.
    source: oneOf(o.source ?? o.mode, HOLIDAY_TYPE_SOURCES, 'auto'),
    maxItems: clampInt(o.maxItems, 1, MAX_MENU_ITEMS, 5),
    sub: toText(o.sub, MAX_SUB),
    items,
    footerLink: footerLink(o.footerLink),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeCustomMenu(v, index) {
  const o = v && typeof v === 'object' ? v : {};
  const items = (Array.isArray(o.items) ? o.items : [])
    .map((it, i) => ({
      id: itemId(it?.id, `i${i + 1}`),
      active: bool(it?.active, true),
      label: toText(it?.label),
      sub: toText(it?.sub, MAX_SUB),
      icon: iconName(it?.icon),
      url: safeUrl(it?.url),
      newTab: bool(it?.newTab, false),
    }))
    .filter((it) => it.url && (it.label.nl || it.label.en))
    .slice(0, MAX_MENU_ITEMS);
  return {
    id: itemId(o.id, `menu${index + 1}`),
    enabled: bool(o.enabled, true),
    label: toText(o.label),
    glyph: iconName(o.glyph),
    items: uniqueIds(items),
    footerLink: footerLink(o.footerLink),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeMenus(v, legacy) {
  const o = v && typeof v === 'object' ? v : {};
  const custom = uniqueIds(
    (Array.isArray(o.custom) ? o.custom : [])
      .map((m, i) => normalizeCustomMenu(m, i))
      .filter((m) => m.label.nl || m.label.en)
      .slice(0, MAX_CUSTOM_MENUS)
  );
  return {
    // Version 1 kept these two at the top level.
    destinations: normalizeDestinationsMenu(o.destinations ?? legacy.popularDestinations),
    holidayTypes: normalizeHolidayTypesMenu(o.holidayTypes ?? legacy.holidayTypes),
    custom,
  };
}

/** Every menu exactly once: unknown keys dropped, forgotten menus appended. */
export function normalizeMenuOrder(v, menus) {
  const keys = ['destinations', 'holidayTypes', ...menus.custom.map((m) => `custom:${m.id}`)];
  const wanted = (Array.isArray(v) ? v : []).filter((k) => keys.includes(k));
  const seen = new Set();
  const out = [];
  for (const k of [...wanted, ...keys]) {
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

function normalizeSearch(v) {
  const o = v && typeof v === 'object' ? v : {};
  return {
    enabled: bool(o.enabled, true),
    placeholder: toText(o.placeholder),
    showSuggestions: bool(o.showSuggestions, true),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeSupport(v) {
  const o = v && typeof v === 'object' ? v : {};
  return {
    enabled: bool(o.enabled, true),
    label: toText(o.label),
    link: safeUrl(o.link) || '/faq',
    newTab: bool(o.newTab, false),
    icon: oneOf(o.icon, SUPPORT_ICONS, 'phone'),
    showLabel: bool(o.showLabel, true),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

// Reviews. `score` is the TrustScore the dashboard copies from the agency's own Trustpilot
// profile (1.0 to 5.0, one decimal); null shows the plain "read our reviews" link instead. Never
// a default: a review score is a real company's reputation, so it is only ever what the editor
// typed from the real profile.
function normalizeTrustpilot(v) {
  const o = v && typeof v === 'object' ? v : {};
  const n = o.score === '' || o.score == null ? NaN : Number(String(o.score).replace(',', '.'));
  return {
    enabled: bool(o.enabled, true),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
    score: Number.isFinite(n) && n >= 1 && n <= 5 ? Math.round(n * 10) / 10 : null,
  };
}

function normalizeSimpleBlock(v, enabledFallback) {
  const o = v && typeof v === 'object' ? v : {};
  return {
    enabled: bool(o.enabled, enabledFallback),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

// Language and account used to live in their own columns. They are the fallback here, so a
// row the first header screen saved keeps what it had until this screen saves it once.
function normalizeAccount(v, columns) {
  const o = v && typeof v === 'object' ? v : {};
  const stored = new Map();
  for (const m of Array.isArray(o.menuItems) ? o.menuItems : []) {
    if (ACCOUNT_MENU_KEYS.includes(m?.key) && !stored.has(m.key)) stored.set(m.key, m);
  }
  const order = [
    ...(Array.isArray(o.menuItems) ? o.menuItems.map((m) => m?.key) : []),
    ...ACCOUNT_MENU_KEYS,
  ].filter((k, i, all) => ACCOUNT_MENU_KEYS.includes(k) && all.indexOf(k) === i);
  const register = o.register && typeof o.register === 'object' ? o.register : {};
  return {
    enabled: bool(o.enabled, columns.enableAccountMenu !== false),
    signInLabel: toText(o.signInLabel),
    signInUrl: safeUrl(o.signInUrl) || safeUrl(columns.loginUrl) || '/login',
    register: {
      enabled: bool(register.enabled, true),
      label: toText(register.label),
      url: safeUrl(register.url) || '/register',
    },
    fallbackName: toText(o.fallbackName),
    menuItems: order.map((key) => ({
      key,
      enabled: bool(stored.get(key)?.enabled, true),
      label: toText(stored.get(key)?.label),
    })),
    showOn: oneOf(o.showOn, SHOW_ON, 'all'),
  };
}

function normalizeMobileMenu(v) {
  const o = v && typeof v === 'object' ? v : {};
  const hasItems = Array.isArray(o.items);
  const raw = hasItems ? o.items : SERVICE_ITEMS.map((s) => ({ key: s.key, id: s.key }));
  const items = raw
    .map((it, i) => {
      const key = SERVICE_URL.has(it?.key) ? it.key : '';
      return {
        id: itemId(it?.id, key || `m${i + 1}`),
        key,
        active: bool(it?.active, true),
        label: toText(it?.label),
        icon: iconName(it?.icon),
        url: safeUrl(it?.url) || (key ? SERVICE_URL.get(key) : ''),
        newTab: bool(it?.newTab, false),
      };
    })
    .filter((it) => it.url && (it.key || it.label.nl || it.label.en))
    .slice(0, MAX_MOBILE_ITEMS);
  return {
    items: uniqueIds(items),
    showMenus: bool(o.showMenus, true),
    showSearch: bool(o.showSearch, true),
  };
}

// The wordmark beside the logo icon (server: siteHeader.js normalizeWordmark).
// Height in px on the desktop bar (the phone bar caps it at 36). Levent 24 Oct: bigger, 46.
export const WORDMARK_HEIGHT = { min: 20, max: 64, fallback: 46 };
// The logo's height in px; null = automatic (the website's 40px icon / wide logo up to 64px).
export const LOGO_HEIGHT = { min: 20, max: 80 };
function normalizeLogoBlock(v) {
  const o = v && typeof v === 'object' ? v : {};
  const n = o.height === '' || o.height == null ? NaN : Number(o.height);
  return { height: Number.isFinite(n) ? Math.min(LOGO_HEIGHT.max, Math.max(LOGO_HEIGHT.min, Math.round(n))) : null };
}
function normalizeWordmark(v) {
  const o = v && typeof v === 'object' ? v : {};
  const n = Number(o.height);
  const height = Number.isFinite(n) ? Math.min(WORDMARK_HEIGHT.max, Math.max(WORDMARK_HEIGHT.min, Math.round(n))) : WORDMARK_HEIGHT.fallback;
  return { url: safeUrl(o.url), alt: str(o.alt, 255), height };
}

/**
 * The complete settings, exactly as the server would normalise them.
 *
 * @param value   stored siteHeader (version 1, version 2 or null)
 * @param columns the header_configs row, for the language/account fallbacks
 */
export function normalizeSiteHeader(value, columns = {}) {
  const v = value && typeof value === 'object' ? value : {};
  const cols = columns && typeof columns === 'object' ? columns : {};
  const menus = normalizeMenus(v.menus, v);
  return {
    version: SITE_HEADER_VERSION,
    announcement: normalizeAnnouncement(v.announcement),
    wordmark: normalizeWordmark(v.wordmark),
    logo: normalizeLogoBlock(v.logo),
    menus,
    menuOrder: normalizeMenuOrder(v.menuOrder, menus),
    search: normalizeSearch(v.search),
    support: normalizeSupport(v.support),
    trustpilot: normalizeTrustpilot(v.trustpilot),
    language: normalizeSimpleBlock(v.language, cols.enableLanguageSelector !== false),
    account: normalizeAccount(v.account, cols),
    mobileMenu: normalizeMobileMenu(v.mobileMenu),
  };
}

/** The settings without the parts only the server computes, ready to PUT. */
export function stripServerFields(siteHeader) {
  const sh = siteHeader || {};
  const { live, ...announcement } = sh.announcement || {};
  const { resolved, ...holidayTypes } = sh.menus?.holidayTypes || {};
  return {
    ...sh,
    version: SITE_HEADER_VERSION,
    announcement,
    menus: { ...(sh.menus || {}), holidayTypes },
  };
}

// ─── editing helpers ────────────────────────────────────────────────────────

/** The first `${prefix}${n}` not already taken, for a new row's id. */
export const nextId = (prefix, taken) => {
  const used = new Set(taken);
  let n = 1;
  while (used.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
};

export const moveItem = (list, index, dir) => {
  const target = index + dir;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export const customMenuKey = (id) => `custom:${id}`;

export const newDestinationItem = (taken, patch = {}) => ({
  id: nextId('d', taken),
  active: true,
  label: emptyText(),
  sub: emptyText(),
  icon: '',
  dest: null,
  boardCode: '',
  holidayTypeId: null,
  url: '',
  ...patch,
});

export const newCustomMenu = (taken) => ({
  id: nextId('menu', taken),
  enabled: true,
  label: emptyText(),
  glyph: '',
  items: [],
  footerLink: { enabled: false, label: emptyText(), url: '' },
  showOn: 'all',
});

export const newCustomItem = (taken) => ({
  id: nextId('i', taken),
  active: true,
  label: emptyText(),
  sub: emptyText(),
  icon: '',
  url: '',
  newTab: false,
});

export const newMobileItem = (taken, key = '') => {
  const service = SERVICE_ITEMS.find((s) => s.key === key);
  return {
    id: service && !taken.includes(key) ? key : nextId('m', taken),
    key: service ? key : '',
    active: true,
    label: emptyText(),
    icon: '',
    url: service ? service.url : '',
    newTab: false,
  };
};

/** Today in Brussels, YYYY-MM-DD: the day the server judges the announcement window by. */
export const brusselsToday = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Brussels',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

/**
 * Where the announcement stands today: 'off', 'empty' (on, but no text), 'scheduled' (before
 * its start), 'ended' (after its end) or 'live'. Only 'live' reaches visitors, which is the
 * server's isAnnouncementLive.
 */
export function announcementStatus(a, today) {
  if (!a?.enabled) return 'off';
  if (!hasText(a.text)) return 'empty';
  if (a.startDate && today < a.startDate) return 'scheduled';
  if (a.endDate && today > a.endDate) return 'ended';
  return 'live';
}

// ─── validation ─────────────────────────────────────────────────────────────

// Section ids, shared with the page's navigation so an error can point at its card.
export const SECTION = {
  logo: 'logo',
  announcement: 'announcement',
  menus: 'menus',
  search: 'search',
  support: 'support',
  reviews: 'reviews',
  language: 'language',
  account: 'account',
  mobile: 'mobile',
};

const LINK_HINT = 'start with /, #, https://, mailto: or tel:';

/**
 * Everything the server would silently drop or replace on save, as
 * [{ section, path, message }]. `path` names the field so the editor can mark it.
 *
 * The rules are the contract's: a custom menu needs a label, a custom row a label and a link,
 * a destination row something to open; every link must be one the server keeps.
 */
export function validateSiteHeader(sh) {
  const errors = [];
  const add = (section, path, message) => errors.push({ section, path, message });
  const checkLink = (section, path, value, what) => {
    if (!isValidLink(value)) add(section, path, `${what}: the link must ${LINK_HINT}.`);
  };
  const checkFooter = (path, f, menuName) => {
    if (!f) return;
    if (f.enabled && !String(f.url ?? '').trim()) {
      add(SECTION.menus, `${path}.footerLink.url`, `${menuName}: the "see all" row needs a link.`);
    } else {
      checkLink(SECTION.menus, `${path}.footerLink.url`, f.url, `${menuName} "see all" row`);
    }
  };

  const a = sh.announcement || {};
  if (a.enabled && !hasText(a.text)) {
    add(SECTION.announcement, 'announcement.text', 'Announcement bar: add a text (NL or EN) or switch the bar off.');
  }
  checkLink(SECTION.announcement, 'announcement.link', a.link, 'Announcement bar');
  if (hasText(a.linkLabel) && !String(a.link ?? '').trim()) {
    add(SECTION.announcement, 'announcement.link', 'Announcement bar: the link text needs a link.');
  }
  if (a.startDate && a.endDate && a.endDate < a.startDate) {
    add(SECTION.announcement, 'announcement.endDate', 'Announcement bar: the end date is before the start date.');
  }

  const d = sh.menus?.destinations || {};
  (d.items || []).forEach((it, i) => {
    const base = `menus.destinations.items.${it.id}`;
    if (!it.dest && !String(it.url ?? '').trim() && !it.holidayTypeId && !it.boardCode) {
      add(SECTION.menus, base, `Destinations menu, row ${i + 1}: pick a place, board type or holiday type, or add a link.`);
    }
    // A row with no place has nothing to be named after, so it would show up blank.
    if (!it.dest && !hasText(it.label)) {
      add(SECTION.menus, `${base}.label`, `Destinations menu, row ${i + 1}: add a label (there is no place to name it after).`);
    }
    checkLink(SECTION.menus, `${base}.url`, it.url, `Destinations menu, row ${i + 1}`);
  });
  checkFooter('menus.destinations', d.footerLink, 'Destinations menu');
  checkFooter('menus.holidayTypes', sh.menus?.holidayTypes?.footerLink, 'Holiday types menu');

  (sh.menus?.custom || []).forEach((m, mi) => {
    const base = `menus.custom.${m.id}`;
    const name = hasText(m.label) ? `Menu "${m.label.en || m.label.nl}"` : `Custom menu ${mi + 1}`;
    if (!hasText(m.label)) add(SECTION.menus, `${base}.label`, `${name}: give the menu a label.`);
    (m.items || []).forEach((it, i) => {
      const ib = `${base}.items.${it.id}`;
      if (!hasText(it.label)) add(SECTION.menus, `${ib}.label`, `${name}, row ${i + 1}: add a label.`);
      if (!String(it.url ?? '').trim()) add(SECTION.menus, `${ib}.url`, `${name}, row ${i + 1}: add a link.`);
      else checkLink(SECTION.menus, `${ib}.url`, it.url, `${name}, row ${i + 1}`);
    });
    checkFooter(base, m.footerLink, name);
  });

  checkLink(SECTION.support, 'support.link', sh.support?.link, 'Customer service');
  // The server would drop an out-of-range score without a word; say so before saving instead.
  const rawScore = sh.trustpilot?.score;
  if (rawScore !== null && rawScore !== undefined && String(rawScore).trim() !== '') {
    const n = Number(String(rawScore).replace(',', '.'));
    if (!Number.isFinite(n) || n < 1 || n > 5) {
      add(SECTION.reviews, 'trustpilot.score', 'Reviews: the TrustScore must be between 1.0 and 5.0, as on your Trustpilot page (or empty).');
    }
  }
  checkLink(SECTION.account, 'account.signInUrl', sh.account?.signInUrl, 'Sign in');
  checkLink(SECTION.account, 'account.register.url', sh.account?.register?.url, 'Register');

  (sh.mobileMenu?.items || []).forEach((it, i) => {
    const base = `mobileMenu.items.${it.id}`;
    if (!it.key) {
      if (!hasText(it.label)) add(SECTION.mobile, `${base}.label`, `Mobile menu, row ${i + 1}: add a label.`);
      if (!String(it.url ?? '').trim()) add(SECTION.mobile, `${base}.url`, `Mobile menu, row ${i + 1}: add a link.`);
    }
    checkLink(SECTION.mobile, `${base}.url`, it.url, `Mobile menu, row ${i + 1}`);
  });

  return errors;
}
